/**
 * @file Tasks.tsx
 * @description "Zadania" — full task list for the life layer (GET/POST/PATCH
 * /api/tasks). Two sections: open tasks (due first, then priority) and the
 * last 15 completed ones, plus a new-task form (title, domain, priority,
 * due datetime-local, recurrence). Polled every 30 seconds like Inbox.tsx.
 * All page copy is intentionally Polish (tasks i18n namespace) — same
 * convention as Inbox.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Clock, ListTodo, Plus, RefreshCw } from "lucide-react";
import { api, LIFE_DOMAINS, LIFE_TASK_PRIORITIES, LIFE_TASK_RECURRENCES } from "../lib/api";
import type { LifeTask, LifeTaskPriority, LifeTaskRecurrence } from "../lib/api";
import { domainMap, localInputToIso } from "../lib/life";
import { LifeTaskRow } from "../components/LifeTaskRow";
import { EmptyState } from "../components/EmptyState";

const POLL_INTERVAL_MS = 30_000;

interface NewTaskDraft {
  title: string;
  domain: string; // "" = none
  priority: LifeTaskPriority;
  dueAt: string; // datetime-local value ("" = none)
  recurrence: "" | LifeTaskRecurrence; // "" = none
}

const EMPTY_DRAFT: NewTaskDraft = {
  title: "",
  domain: "",
  priority: "MEDIUM",
  dueAt: "",
  recurrence: "",
};

export function Tasks() {
  const { t } = useTranslation("tasks");
  const [open, setOpen] = useState<LifeTask[] | null>(null);
  const [done, setDone] = useState<LifeTask[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [draft, setDraft] = useState<NewTaskDraft>(EMPTY_DRAFT);
  const [submitting, setSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const rawScope = searchParams.get("scope");
  const scope = rawScope === "praca" || rawScope === "prywatne" ? rawScope : undefined;

  const domains = useMemo(() => domainMap(LIFE_DOMAINS), []);
  const selectedDomain = draft.domain ? domains.get(draft.domain) : undefined;

  const load = useCallback(async () => {
    try {
      const [openRes, doneRes] = await Promise.all([
        api.tasks.list("open", scope),
        api.tasks.list("done", scope),
      ]);
      setOpen(openRes.tasks);
      setDone(doneRes.tasks);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    load();
    const interval = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  const withBusy = useCallback(
    async (id: string, action: () => Promise<unknown>) => {
      setBusyId(id);
      setActionError(null);
      try {
        await action();
        await load();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyId(null);
      }
    },
    [load]
  );

  const onToggleDone = useCallback(
    (id: string) => withBusy(id, () => api.tasks.toggleDone(id)),
    [withBusy]
  );
  const onToggleTop3 = useCallback(
    (id: string) => withBusy(id, () => api.tasks.toggleTop3(id)),
    [withBusy]
  );

  const onCreate = useCallback(async () => {
    if (!draft.title.trim()) return;
    setSubmitting(true);
    setActionError(null);
    try {
      await api.tasks.create({
        title: draft.title.trim(),
        domain: draft.domain || null,
        priority: draft.priority,
        dueAt: localInputToIso(draft.dueAt),
        recurrence: draft.recurrence || null,
      });
      setDraft(EMPTY_DRAFT);
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }, [draft, load]);

  const fieldClass =
    "mt-1 w-full rounded-md border border-border bg-surface-1 px-2 py-1.5 text-xs text-gray-200";
  const labelClass = "block text-[10px] font-semibold uppercase tracking-wider text-gray-500";

  return (
    <div className="animate-fade-in space-y-8">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-accent/15 flex items-center justify-center flex-shrink-0">
            <ListTodo className="w-4.5 h-4.5 text-accent" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-100">{t("title")}</h1>
            <p className="text-xs text-gray-500">{t("subtitle")}</p>
          </div>
        </div>
        <button onClick={load} className="btn-ghost" disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          {t("common:refresh")}
        </button>
      </div>

      {/* Action error banner */}
      {actionError && (
        <div className="card p-3 flex items-center gap-3 border-amber-500/30">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <p className="text-xs text-gray-300 min-w-0 truncate">
            {t("actionError")}: <span className="font-mono text-gray-500">{actionError}</span>
          </p>
        </div>
      )}

      {/* Error state (endpoint unreachable) */}
      {error && !open && (
        <div className="card p-4 flex items-center gap-3 border-red-500/30">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm text-gray-200">{t("loadError")}</p>
            <p className="text-xs text-gray-500 font-mono truncate">{error}</p>
          </div>
        </div>
      )}

      <div className="max-w-3xl space-y-8">
        {/* New-task form */}
        <div className="card p-4 space-y-3">
          <h2 className="text-sm font-semibold text-gray-100">{t("formTitle")}</h2>
          <label className={labelClass}>
            {t("fieldTitle")}
            <input
              type="text"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") onCreate();
              }}
              className={fieldClass}
            />
          </label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <label className={labelClass}>
              {t("fieldDomain")}
              <span className="mt-1 flex items-center gap-1.5">
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0 border border-border"
                  style={{ backgroundColor: selectedDomain?.color ?? "transparent" }}
                  aria-hidden="true"
                />
                <select
                  value={draft.domain}
                  onChange={(e) => setDraft({ ...draft, domain: e.target.value })}
                  className={`${fieldClass} mt-0 flex-1`}
                >
                  <option value="">{t("noDomain")}</option>
                  {LIFE_DOMAINS.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </span>
            </label>
            <label className={labelClass}>
              {t("fieldPriority")}
              <select
                value={draft.priority}
                onChange={(e) =>
                  setDraft({ ...draft, priority: e.target.value as LifeTaskPriority })
                }
                className={fieldClass}
              >
                {LIFE_TASK_PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {t(`priorityNames.${p}`)}
                  </option>
                ))}
              </select>
            </label>
            <label className={labelClass}>
              {t("fieldDueAt")}
              <input
                type="datetime-local"
                value={draft.dueAt}
                onChange={(e) => setDraft({ ...draft, dueAt: e.target.value })}
                className={fieldClass}
              />
            </label>
            <label className={labelClass}>
              {t("fieldRecurrence")}
              <select
                value={draft.recurrence}
                onChange={(e) =>
                  setDraft({ ...draft, recurrence: e.target.value as NewTaskDraft["recurrence"] })
                }
                className={fieldClass}
              >
                <option value="">{t("recurrenceNames.none")}</option>
                {LIFE_TASK_RECURRENCES.map((r) => (
                  <option key={r} value={r}>
                    {t(`recurrenceNames.${r}`)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <button
            onClick={onCreate}
            disabled={submitting || !draft.title.trim()}
            className="btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            <Plus className="w-3.5 h-3.5" />
            {t("actionAdd")}
          </button>
        </div>

        {/* Loading skeleton */}
        {loading && !open && !error && (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse h-16 bg-surface-2 rounded-xl" />
            ))}
          </div>
        )}

        {/* Open tasks */}
        {open && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-accent" aria-hidden />
              <h2 className="text-sm font-semibold text-gray-100">{t("openTitle")}</h2>
              <span className="text-xs text-gray-500">({open.length})</span>
            </div>
            {open.length > 0 ? (
              <div className="space-y-2">
                {open.map((task) => (
                  <LifeTaskRow
                    key={task.id}
                    task={task}
                    domains={domains}
                    busy={busyId === task.id}
                    onToggleDone={onToggleDone}
                    onToggleTop3={onToggleTop3}
                  />
                ))}
              </div>
            ) : (
              <EmptyState icon={ListTodo} title={t("openEmpty")} description={t("openEmptyHint")} />
            )}
          </section>
        )}

        {/* Last 15 done */}
        {done && done.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" aria-hidden />
              <h2 className="text-sm font-semibold text-gray-100">{t("doneTitle")}</h2>
            </div>
            <div className="space-y-2">
              {done.map((task) => (
                <LifeTaskRow
                  key={task.id}
                  task={task}
                  domains={domains}
                  busy={busyId === task.id}
                  onToggleDone={onToggleDone}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
